import React, { ComponentType, lazy } from "react";
import { StaleVersionAutoReload, VersionMismatchFallback } from "@/components/VersionMismatchFallback";
import { isChunkLoadError } from "./errorClassification";
import { LAZY_MODULE_LOADED_EVENT } from "./staleUpdateNavigation";
import {
  IS_DEVELOPMENT,
  attemptStaleRecoveryReload,
  getIsOutdated,
  handleChunkLoadError,
} from "./versionManager";

interface LazyWithRetryOptions {
  retries?: number;
  retryDelay?: number;
}

const DEFAULT_OPTIONS: Required<LazyWithRetryOptions> = {
  retries: 2,
  retryDelay: 1000,
};

/**
 * Deliberately broader than versionManager.handleChunkLoadError, which answers
 * false when the server version looks unchanged even though the visitor still
 * cannot load the chunk. In production a chunk-shaped failure must never reach
 * an error boundary, because the boundary renders the 404 page. In dev a
 * failed chunk is HMR noise, so the gate stays closed.
 */
export function isChunkLoadErrorMessage(message: string): boolean {
  if (IS_DEVELOPMENT) return false;
  return isChunkLoadError({ message });
}

export type PreloadableLazy<T extends ComponentType<any>> = React.LazyExoticComponent<T> & {
  /** Starts (or joins) the chunk download. Safe to call on every hover. */
  preload: () => Promise<unknown>;
};

// React.lazy suspends on first render even when the module promise has already
// settled, because it reads the result in a .then callback. A thenable that
// calls back synchronously lets a preloaded route render on its very first
// commit, which is what keeps a view transition from snapshotting an empty
// Suspense fallback.
const settledThenable = <M,>(module: M) => ({
  then(resolve: (value: M) => void) {
    resolve(module);
  },
});

export function lazyWithRetry<T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
  options: LazyWithRetryOptions = {},
): PreloadableLazy<T> {
  const { retries, retryDelay } = { ...DEFAULT_OPTIONS, ...options };

  let cachedModule: { default: T } | null = null;
  let preloading: Promise<unknown> | null = null;

  const component = lazy(() =>
    cachedModule ? (settledThenable(cachedModule) as unknown as Promise<{ default: T }>) : loadWithRetry(),
  ) as PreloadableLazy<T>;

  // A preload is a hint, so it never retries or reloads the tab; a real render
  // that needs the chunk still goes through the recovery path below.
  component.preload = () => {
    if (cachedModule || getIsOutdated()) return Promise.resolve();
    if (!preloading) {
      preloading = importFn()
        .then((module) => {
          if (module && typeof (module as { default?: unknown }).default !== "undefined") cachedModule = module;
        })
        .catch(() => {
          preloading = null;
        });
    }
    return preloading;
  };
  return component;

  async function loadWithRetry(): Promise<{ default: T }> {
    let lastError: Error | null = null;

    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        if (attempt > 0) {
          await new Promise((resolve) => setTimeout(resolve, retryDelay));
        }

        const module = await importFn();
        if (!module || typeof (module as { default?: unknown }).default === "undefined") {
          // A module that loads but exposes no default export produces an
          // opaque React #306 in a minified build. Throwing here names the
          // import and routes it through the recovery path below.
          throw new Error(
            `[LazyLoad] Module loaded but has no default export: ${importFn.toString().slice(0, 200)}`,
          );
        }

        cachedModule = module;
        if (typeof window !== "undefined") {
          window.dispatchEvent(new CustomEvent(LAZY_MODULE_LOADED_EVENT));
        }
        return module;
      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error));
        console.warn(`[LazyLoad] Import failed (attempt ${attempt + 1}):`, lastError.message);

        if (attempt === retries) {
          // The version check inside can answer false even for a real stale
          // deploy (the version.json fetch itself can fail on a flaky mobile
          // connection), so a chunk-shaped error still recovers below.
          const wasHandled = await handleChunkLoadError(lastError);

          if (wasHandled || isChunkLoadErrorMessage(lastError.message)) {
            if (cachedModule) return cachedModule;

            // The URL already points at the destination route, so a hard
            // reload fetches the fresh deploy and lands the visitor exactly
            // where they were going, with no interaction.
            if (attemptStaleRecoveryReload()) {
              return { default: StaleVersionAutoReload as unknown as T };
            }

            return { default: VersionMismatchFallback as unknown as T };
          }
        }
      }
    }

    throw lastError || new Error("Failed to load component");
  }
}

export function preloadComponent<T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
): void {
  if (getIsOutdated()) return;

  importFn().catch((error) => {
    console.warn("[Preload] Failed to preload component:", error);
    void handleChunkLoadError(error instanceof Error ? error : new Error(String(error)));
  });
}
